import React from 'react'
import { Columns, Column } from 'react-flex-columns'
import { useMediaLayout } from 'use-media'
import Centered from 'shared/ui/Centered'
import Tiles from 'shared/ui/Tiles'
import TweetEmbed from 'shared/ui/TweetEmbed'
import tweetIds from '../data/tweetIds'
import Icon from 'shared/ui/Icon'
import { IconContent, IconContentText } from 'shared/ui/IconContent'
import Heading from 'shared/ui/Heading'
import ContentTile from 'shared/ui/ContentTile'
import { IconList, IconListItem, Icon as IconListIcon } from 'shared/ui/IconList'
import { Hero, HeroPrimaryContent } from '../ui/Hero'
import Card, { CardFullBleedImageHeader } from '../ui/Card'
import TrainingContactForm from '../ui/forms/TrainingContactForm'
import { SlantBackground, SlantBackgroundImage } from '../ui/SlantBackground'
import { NewsletterSubscribeSection } from '../ui/NewsletterSubscribeForm'
import TopBrands from '../ui/brands/TopBrands'

export default function CorporateTraining() {
  const mobileView = useMediaLayout({ maxWidth: '900px' })

  return (
    <div className="workshops-page spacing-giant">
      <Hero>
        <HeroPrimaryContent>
          <div className="spacing">
            <Heading className="text-color-blue">
              <span className="text-xlarge">Corporate Training</span>
            </Heading>
            <div className="text-large">
              Get your team professionally trained in React, JavaScript and Node
            </div>
          </div>
          <p>
            Remote trainings are always available, and in-person trainings resume after it's safe
            with COVID-19.
          </p>
          <div>
            <a href="#contact" className="button">
              Plan a Training
            </a>
          </div>
        </HeroPrimaryContent>
      </Hero>

      <SlantBackground verticalOffset={-7} shallowSlant>
        <SlantBackgroundImage src="/images/react-gray-two.svg" />
      </SlantBackground>

      <Centered>
        <div className="spacing-giant">
          <section className="offset-slant-background-image-left">
            <Heading size={2}>
              After our training, your team will be <Nobr>able to:</Nobr>
            </Heading>
            <ul className="list-reset spacing">
              <IconContent as="li">
                <IconContentText as="strong">
                  <Icon name="arrow-right" size={1.5} color="brightBlue" />
                  Simplify their code!
                </IconContentText>
              </IconContent>
              <IconContent as="li">
                <IconContentText as="strong">
                  <Icon name="arrow-right" size={1.5} color="brightBlue" />
                  Level up their React proficiency
                </IconContentText>
              </IconContent>
              <IconContent as="li">
                <IconContentText as="strong">
                  <Icon name="arrow-right" size={1.5} color="brightBlue" />
                  Catered to different skills
                </IconContentText>
              </IconContent>
              <IconContent as="li">
                <IconContentText as="strong">
                  <Icon name="arrow-right" size={1.5} color="brightBlue" />
                  Jump-start a migration to React
                </IconContentText>
              </IconContent>
              <IconContent as="li">
                <IconContentText as="strong">
                  <Icon name="arrow-right" size={1.5} color="brightBlue" />
                  Meet their development goals
                </IconContentText>
              </IconContent>
              <IconContent as="li">
                <IconContentText as="strong">
                  <Icon name="arrow-right" size={1.5} color="brightBlue" />
                  Overcome challenges they've been wrestling with
                </IconContentText>
              </IconContent>
              <IconContent as="li">
                <IconContentText as="strong">
                  <Icon name="arrow-right" size={1.5} color="brightBlue" />
                  Get on the same technical page
                </IconContentText>
              </IconContent>
            </ul>
          </section>

          <Card>
            <TopBrands />
          </Card>
        </div>
      </Centered>

      <SlantBackground verticalOffset={-5} slantRight />

      <Centered>
        <div className="spacing-giant">
          <section className="spacing-large">
            <Columns gutterSize={4} stack={mobileView}>
              <Column size={22}>
                <div className="spacing-giant">
                  <Heading id="about-workshops" size={2}>
                    <span className="text-xlarge">What are the workshops like?</span>
                  </Heading>

                  <ContentTile title="Two or Three-Day Workshops">
                    <p>
                      We offer in-person and remote trainings. Both the instructor and attendees
                      will be writing code to build parts of a real project.
                    </p>
                    <ul className="list-reset spacing">
                      <IconContent as="li" className="spacing-small">
                        <IconContentText as="strong">
                          <Icon name="arrow-right" size={1.5} color="brightBlue" />
                          Work on a real project
                        </IconContentText>
                        <div>With guided lectures for two days.</div>
                      </IconContent>

                      <IconContent as="li" className="spacing-small">
                        <IconContentText as="strong">
                          <Icon name="arrow-right" size={1.5} color="brightBlue" />
                          Deepen Knowledge of API,
                        </IconContentText>
                        <div>Best Practices, and Patterns.</div>
                      </IconContent>

                      <IconContent as="li" className="spacing-small">
                        <IconContentText as="strong">
                          <Icon name="arrow-right" size={1.5} color="brightBlue" />
                          Catered to different skills
                        </IconContentText>
                        <div>Core concepts and advanced</div>
                      </IconContent>

                      <IconContent as="li" className="spacing-small">
                        <IconContentText as="strong">
                          <Icon name="arrow-right" size={1.5} color="brightBlue" />
                          Have Lots of Fun!
                        </IconContentText>
                        <div>It's easier to remember content when we're having fun.</div>
                      </IconContent>
                    </ul>
                  </ContentTile>
                </div>
              </Column>
              <Column flex>
                <Card id="contact">
                  <CardFullBleedImageHeader src="/images/workshop-color-small.png" alt="Workshop" />
                  <TrainingContactForm />
                </Card>
              </Column>
            </Columns>

            <Heading as="h2" size={2}>
              Beginner Team? Advanced Team? Or Mixed?
            </Heading>

            <p>
              Our curriculum is designed to teach in-depth. We've found that this is great for
              beginners but also, those who have been doing React for a while regularly compliment
              the curriculum and teaching style
            </p>

            <Heading as="h2" size={2}>
              Material
            </Heading>

            <p>
              Here's an overview of just some of the topics and concepts available in our
              curriculum:
            </p>

            <Tiles minSize={20}>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">All Hooks Based</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Custom Hooks</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Declarative Mental Model</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Local and Application State</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Redux compared to React Context</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Context Abstractions</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Side-Effects (Network API Calls and more)</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Data Flow</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">React Router (Single Page Applications)</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Authentication</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Component Abstractions</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Compound Components</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Accessibility</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Controlled and Uncontrolled Form Fields</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Controlled and Uncontrolled Components</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Performance Optimizations</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Animation</span>
              </div>
              <div className="icon-text">
                <Icon name="check-circle" size={1} color="green" />
                <span className="text-small">Unit, Integration Testing</span>
              </div>
            </Tiles>
          </section>
        </div>
      </Centered>

      <SlantBackground verticalOffset={-3} shallowSlant />

      <Centered>
        <section>
          <Card>
            <Tiles gutter={1.5} minSize={13}>
              {tweetIds.slice(0, 9).map((id) => (
                <TweetEmbed
                  key={id}
                  options={{
                    dnt: true,
                    cards: 'hidden',
                    conversation: 'none',
                  }}
                  id={id}
                />
              ))}
            </Tiles>
          </Card>
        </section>
      </Centered>

      <SlantBackground slantRight verticalOffset={-6} shallowSlant />

      <Centered>
        <div className="spacing-giant">
          <section>
            <Columns gutters stack={mobileView}>
              <Column size={25}>
                <div className="spacing">
                  <Heading size={3} id="react-workshop">
                    React Workshop
                  </Heading>
                  <p>
                    A person who knows little-to-nothing about React should expect to be fairly
                    proficient after taking our Workshop. More experienced React developers should
                    expect to level up in terms of professional architecture concepts and component
                    design.
                  </p>
                  <IconList spacing="small" iconName="check-circle" iconColor="brightBlue">
                    <IconListItem>
                      <IconListIcon />
                      <span className="text-small">All about React Hooks</span>
                    </IconListItem>
                    <IconListItem>
                      <IconListIcon />
                      <span className="text-small">Fundamentals and advanced material</span>
                    </IconListItem>
                    <IconListItem>
                      <IconListIcon />
                      <span className="text-small">Component design best practices</span>
                    </IconListItem>
                    <IconListItem>
                      <IconListIcon />
                      <span className="text-small">Free online resources after workshop</span>
                    </IconListItem>
                  </IconList>
                </div>
              </Column>
              {!mobileView && (
                <Column flex>
                  <img
                    src="/images/tree-background.svg"
                    className="react-content-graphic"
                    alt="React as nodes"
                  />
                </Column>
              )}
            </Columns>
          </section>

          <section>
            <Columns gutters stack={mobileView}>
              {!mobileView && (
                <Column flex>
                  <img
                    src="/images/nodejs-background.svg"
                    className="node-content-graphic"
                    alt="Node Logo"
                  />
                </Column>
              )}
              <Column size={25}>
                <div className="spacing">
                  <Heading size={3} id="javascript-workshop">
                    JavaScript / Node Workshop
                  </Heading>
                  <p>
                    The JavaScript / Node workshop is not <em>programming 101</em>. The material
                    assumes some proficiencies in programming (in any language), but you still feel
                    like you want to improve your JavaScript skills. Aside from modern JavaScript
                    and build processes, we'll cover the most fundamental concepts of Node as well
                    as some advanced ones.
                  </p>
                  <IconList spacing="small" iconName="check-circle" iconColor="brightBlue">
                    <IconListItem>
                      <IconListIcon />
                      <span className="text-small">Node best practices and principles</span>
                    </IconListItem>
                    <IconListItem>
                      <IconListIcon />
                      <span className="text-small">
                        The Node runtime, built-in capabilities, and NPM/Yarn
                      </span>
                    </IconListItem>
                    <IconListItem>
                      <IconListIcon />
                      <span className="text-small">
                        API design/organization with Express.js & middleware
                      </span>
                    </IconListItem>
                    <IconListItem>
                      <IconListIcon />
                      <span className="text-small">
                        Async database connections: MongoDB, PostreSQL, MySQL
                      </span>
                    </IconListItem>
                    <IconListItem>
                      <IconListIcon />
                      <span className="text-small">
                        Non IO-blocking concepts like promises & async/await
                      </span>
                    </IconListItem>
                  </IconList>
                </div>
              </Column>
            </Columns>
          </section>

          <hr />
          <section className="spacing-large">
            <Heading as="h2" size={3}>
              Questions?
            </Heading>

            <div className="spacing-small">
              <Heading as="h3" size={4}>
                What is the format for the workshops? Remote or in-person?
              </Heading>
              <p>
                The workshop is typically two days and for about seven hours (which includes time
                for a one-hour break lunch). It can be done in-person or over video conferencing
                like with Zoom. The instructor will do all live-coding to demonstrate the topics and
                then the attendees will do exercises to help the material set in.
              </p>
            </div>
            <div className="spacing-small">
              <Heading as="h3" size={4}>
                Can the curriculum be tailored?
              </Heading>
              <p>
                The curriculum isn't set in stone, but we do have a normal flow for the curriculum
                which works really well. However, special requests can be made to substitute parts
                in and out.
              </p>
            </div>
            <div className="spacing-small">
              <Heading as="h3" size={4}>
                What skill level is expected? Is there advanced material?
              </Heading>
              <p>
                Most of the time the workshops are "Core React" which is perfectly appropriate for
                those who are completely new to React and those who have about 1 to 3 years of
                experience. For more advanced groups, we have specific advanced material. We can
                also send a test out ahead of time to help evaluate where your team is and what can
                be adjusted in the curriculum accordingly.
              </p>
            </div>
            <div className="spacing-small">
              <Heading as="h3" size={4}>
                Should the attendee already know JavaScript?
              </Heading>
              <p>
                Not necessarily, as long as they already know how to code in some other language.
                The workshop is not <em>programming 101</em> so those who don't know a lot of code
                will probably struggle. On the other hand, it's fairly normal for our clients to
                hire us because they want to train their Java, C#, or Ruby teams in React. And
                sometimes they're fairly new to JavaScript, but that's why we have a JS primer for
                them to catch up on some things before the workshop.
              </p>
            </div>
          </section>

          <hr />
          <NewsletterSubscribeSection />
        </div>
      </Centered>
    </div>
  )
}

function TextInput(props) {
  return <input type="text" {...props} />
}

function SomeComponent() {
  const inputRef = React.useRef()

  // Let's say you want to focus the input when it is rendered
  React.useEffect(() => {
    // This won't work because we didn't forward the ref!
    inputRef.current.focus()
  }, [])

  return (
    <div>
      <TextInput ref={inputRef} />
    </div>
  )
}

function Nobr(props) {
  return <span {...props} style={{ whiteSpace: 'nowrap', ...props.style }} />
}
